<script lang="ts">
    import {KpPopoverPanel, type TogglePopoverFn} from 'shared/ui-widgets/popover';
    import {getLocalization, getLogger} from 'src/core';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {formControls} from '../../forms/use.form-controls';
    import {touched} from '../../shared/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getContext} from 'svelte';
    import {FormControlBuilder} from '../../forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {onDestroy} from 'svelte';
    import {required} from '../../forms/use.required';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {dateRangeValidatorFactory, dateRangeParser} from './utils';
    import {uuid, exists} from 'shared/utils/custom-utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import type {
        DateRangeValueEditorLocalizations,
        DateRangeValueEditorOptions,
        DateRangeValueEditorValidations
    } from './types';
    import {
        KpDateRangePicker,
        formatIntervalStringToReadableForm,
        type IntervalString
    } from 'shared/ui-widgets/daterange-picker';

    export let model: IntervalString | null;
    export let formControlOptions: FormControlOptions;
    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;
    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;
    export let options: DateRangeValueEditorOptions;
    export let validations: DateRangeValueEditorValidations;

    $: readableModel = exists(model) && model ? formatIntervalStringToReadableForm(model, localization) : '';

    const localize: EditorsLocalizationFunction<DateRangeValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const localization = getLocalization();
    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});
    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(dateRangeValidatorFactory(validations))
        .withParsers([dateRangeParser()])
        .withOptions(formControlOptions)
        // TODO: Add actual modifiers and formatters?
        .withModifiers([(value: IntervalString | null) => value?.trim() ?? null])
        .withFormatters([(value: IntervalString | null) => value?.trim() ?? null])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    });

    let togglePopover: TogglePopoverFn;
    const popoverId = uuid();
    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-end'));

    export function getFormController(): FormControl<string> {
        return formControl;
    }

    const handleIntervalChange = (event: CustomEvent<IntervalString>) => {
        formControl.setViewValue(event.detail);
    };

    const handleClosePopover = () => {
        togglePopover(false);
    };
</script>

<div class="input-group input-group-{size} relative" use:floatingRef>
    <input class="form-control input-{size} secret-input"
           type="text"
           id={editorId}
           name={editorName}
           readonly
           disabled={isDisabled}
           {placeholder}
           use:required={validations.required}
           use:focusOnMount={isFocused}
           use:formControls={formControl}
           use:touched={{ formControl }}
           use:style={{ field: formControl.getFieldStateAsStore() }}
           use:ariaInvalid={formControl}
           use:ariaErrormessage={formControl}
           use:ariaDescribedby={inputDescription}
           data-main-input/>

    <button type="button"
            class="input-{size} date-range-label"
            disabled={isDisabled}
            popovertarget={popoverId}>

        {#if !options.compact}
            <span class="interval-label">{localize('selectedRange')}</span>
        {/if}

        {#if readableModel}
            <span>{readableModel}</span>
        {/if}
    </button>

    <KpButton buttonSize={size} popovertarget={popoverId}>
        <IconedContent icon="calendar-lines">
            {#if options.compact}
                <span class="sr-only">{localize('openRangeSelector')}</span>
            {:else}
                {localize('openRangeSelector')}
            {/if}
        </IconedContent>
    </KpButton>
</div>

<KpPopoverPanel id={popoverId}
                use={[floatingContent]}
                additionalClasses="date-range-picker-popover"
                on:popover-close={() => formControl.setTouched()}
                bind:togglePopover>

    <div class="date-range-picker-container">
        <KpDateRangePicker interval={model}
                           granularity={options.granularity}
                           inPopoverMode
                           on:interval-changed={handleIntervalChange}
                           on:close-click={handleClosePopover}/>
    </div>
</KpPopoverPanel>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .date-range-picker-popover {
            --popover-content-padding: 0;
        }
    }

    .input-group {
        display: flex;
        align-items: center;
        position: relative;
        gap: @spacing-sm;

        .secret-input {
            position: absolute;
            inset: 0;
            opacity: 0;
            pointer-events: none;
        }

        .interval-label {
            color: @themed-text-muted;
        }

        .date-range-label {
            width: 100%;
            display: flex;
            align-items: center;
            gap: @spacing-s;
            outline: none;
            border: none;
            cursor: pointer;
            border-radius: @border-radius-default;
            background-color: transparent;
            transition: background-color 0.3s ease-in-out;

            &:hover {
                background-color: @themed-body-bg-blue-highlighted;
            }
        }
    }
</style>